<template>
    <div class="attachments-container">
        <fieldset>
            <legend class="text-blue">
                <i class="fas fa-paperclip mr-2"></i>
                {{ $t('customerLoanRequest.attachments') }}
            </legend>

            <!-- Drag & Drop Upload Area -->
            <div
                class="upload-area"
                :class="{ 'drag-over': isDragOver }"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="onDrop"
                @click="$refs.fileInput.click()"
            >
                <input
                    type="file"
                    ref="fileInput"
                    multiple
                    @change="onFileSelect"
                    accept=".doc,.csv,.xlsx,.xls,.docx,.ppt,.odt,.ods,.odp,.png,.jpeg,.jpg,.pdf,.txt"
                    class="d-none"
                />
                <div class="upload-content">
                    <i class="fas fa-cloud-upload-alt upload-icon"></i>
                    <p class="upload-text">{{ $t('customerLoanRequest.dragDropText') || 'Drag & drop files here or click to browse' }}</p>
                    <p class="upload-hint">
                        {{ $t('customerLoanRequest.allowedFileTypes') || 'Allowed' }}: PDF, Word, Excel, PowerPoint, Images, Text (Max 10MB)
                    </p>
                </div>
            </div>

            <!-- File List -->
            <div v-if="attachmentList.length > 0" class="file-list mt-4">
                <div class="row">
                    <div
                        v-for="(attachment, index) in attachmentList"
                        :key="index"
                        class="col-md-6 col-lg-4 mb-3"
                    >
                        <div
                            class="file-card"
                            :class="{
                                'file-card-error': attachment.invalid_file,
                                'file-card-uploading': attachment.progressing < 100 && !attachment.uploaded && !attachment.invalid_file
                            }"
                        >
                            <!-- File Icon -->
                            <div class="file-icon-wrapper">
                                <i :class="getFileIcon(attachment.file_type)" class="file-icon"></i>
                            </div>

                            <!-- File Info -->
                            <div class="file-info">
                                <p class="file-name" :title="attachment.file_name">
                                    {{ truncateFileName(attachment.file_name, 25) }}
                                </p>
                                <p class="file-meta">
                                    <span class="file-size">{{ attachment.size_text }}</span>
                                    <span class="file-type-badge">{{ getFileExtension(attachment.file_name).toUpperCase() }}</span>
                                </p>
                            </div>

                            <!-- Upload Progress -->
                            <div v-if="attachment.progressing < 100 && !attachment.uploaded && !attachment.invalid_file" class="file-progress">
                                <div class="progress">
                                    <div
                                        class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                        role="progressbar"
                                        :style="{ width: attachment.progressing + '%' }"
                                    ></div>
                                </div>
                                <span class="progress-text">{{ attachment.progressing }}%</span>
                            </div>

                            <!-- Status Badge -->
                            <div v-else class="file-status">
                                <span v-if="attachment.uploaded" class="badge badge-success">
                                    <i class="fas fa-check mr-1"></i>{{ $t('customerLoanRequest.uploaded') || 'Uploaded' }}
                                </span>
                                <span v-else-if="attachment.invalid_file" class="badge badge-danger">
                                    <i class="fas fa-times mr-1"></i>{{ $t('customerLoanRequest.invalidFile') || 'Invalid' }}
                                </span>
                            </div>

                            <!-- Actions -->
                            <div class="file-actions">
                                <button
                                    v-if="attachment.file_path && attachment.uploaded"
                                    @click.stop="viewFile(attachment.file_path)"
                                    class="btn btn-sm btn-outline-info"
                                    type="button"
                                    :title="$t('customerLoanRequest.view') || 'View'"
                                >
                                    <i class="fas fa-eye"></i>
                                </button>
                                <button
                                    @click.stop="deleteAttachment(index)"
                                    class="btn btn-sm btn-outline-danger"
                                    type="button"
                                    :disabled="attachment.progressing < 100 && !attachment.uploaded && !attachment.invalid_file"
                                    :title="$t('customerLoanRequest.delete') || 'Delete'"
                                >
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state mt-4">
                <i class="fas fa-folder-open empty-icon"></i>
                <p class="empty-text">{{ $t('customerLoanRequest.noAttachments') || 'No attachments yet' }}</p>
            </div>
        </fieldset>

        <!-- Delete Confirmation Modal -->
        <Modal v-model="showDeleteModal" width="400" :mask-closable="false">
            <p slot="header" class="text-center">
                <Icon type="ios-warning" color="#ff9900" size="24"></Icon>
                <span class="ml-2">{{ $t('customerLoanRequest.confirmDelete') || 'Confirm Delete' }}</span>
            </p>
            <div class="text-center py-3">
                <p>{{ $t('customerLoanRequest.confirmDeleteMessage') || 'Are you sure you want to delete this file?' }}</p>
            </div>
            <div slot="footer" class="text-center">
                <Button type="error" @click="confirmDelete">
                    <i class="fas fa-trash mr-1"></i>{{ $t('customerLoanRequest.delete') || 'Delete' }}
                </Button>
                <Button @click="showDeleteModal = false" class="ml-2">
                    {{ $t('customerLoanRequest.cancel') || 'Cancel' }}
                </Button>
            </div>
        </Modal>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import customerLoanRequestAttachment from '@/api/credit-operation/customer-loan-request-attachment';

export default {
    name: 'LoanRequestAttachments',
    props: {
        requestId: {
            type: [String, Number],
            default: null
        },
        isEditMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            attachmentList: [],
            showDeleteModal: false,
            deleteIndex: null,
            isDragOver: false
        };
    },
    watch: {
        requestId: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.loadExistingAttachments();
                }
            }
        }
    },
    methods: {
        ...mapActions('creditOperation/customerLoanRequest', ['storeAttachment', 'deleteAttachment']),

        async loadExistingAttachments() {
            if (!this.requestId) return;

            try {
                const response = await customerLoanRequestAttachment.get(this.requestId);
                if (response && response.data) {
                    this.attachmentList = response.data.map(att => ({
                        attachment_id: att.attachment_id,
                        file_name: att.file_name,
                        file_path: att.file_path,
                        file_type: att.file_type,
                        size: att.file_size,
                        size_text: this.formatFileSize(att.file_size),
                        progressing: 100,
                        uploaded: true,
                        invalid_file: false
                    }));
                }
            } catch (error) {
                this.notice({
                    type: 'error',
                    text: error.message || 'Failed to load attachments'
                });
            }
        },

        onDrop(event) {
            this.isDragOver = false;
            const files = Array.from(event.dataTransfer.files);
            this.processFiles(files);
        },

        onFileSelect(event) {
            const files = Array.from(event.target.files);
            this.processFiles(files);
            this.$refs.fileInput.value = '';
        },

        processFiles(files) {
            files.forEach(file => {
                const isValidType = this.validateFileType(file);
                const isValidSize = this.validateFileSize(file);

                const attachment = {
                    attachment_id: null,
                    file_name: file.name,
                    file_path: null,
                    file_type: file.type || this.getFileExtension(file.name),
                    size: file.size,
                    size_text: this.formatFileSize(file.size),
                    progressing: 0,
                    uploaded: false,
                    invalid_file: !isValidType || !isValidSize,
                    file: file
                };

                this.attachmentList.push(attachment);

                if (!attachment.invalid_file) {
                    this.uploadFile(attachment);
                }
            });
        },

        validateFileType(file) {
            const allowedExtensions = ['doc', 'csv', 'xlsx', 'xls', 'docx', 'ppt', 'odt', 'ods', 'odp', 'png', 'jpeg', 'jpg', 'pdf', 'txt'];
            const extension = file.name.split('.').pop().toLowerCase();
            return allowedExtensions.includes(extension);
        },

        validateFileSize(file) {
            const maxSize = 10 * 1024 * 1024; // 10MB
            return file.size <= maxSize;
        },

        getFileExtension(filename) {
            return filename.split('.').pop().toLowerCase();
        },

        getFileIcon(fileType) {
            const type = (fileType || '').toLowerCase();
            if (type.includes('pdf')) return 'fas fa-file-pdf text-danger';
            if (type.includes('word') || type.includes('doc')) return 'fas fa-file-word text-primary';
            if (type.includes('excel') || type.includes('sheet') || type.includes('xls')) return 'fas fa-file-excel text-success';
            if (type.includes('powerpoint') || type.includes('ppt')) return 'fas fa-file-powerpoint text-warning';
            if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) return 'fas fa-file-image text-info';
            if (type.includes('text') || type.includes('txt')) return 'fas fa-file-alt text-secondary';
            if (type.includes('csv')) return 'fas fa-file-csv text-success';
            return 'fas fa-file text-muted';
        },

        truncateFileName(name, maxLength) {
            if (!name || name.length <= maxLength) return name;
            const ext = this.getFileExtension(name);
            const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
            const truncatedName = nameWithoutExt.substring(0, maxLength - ext.length - 4) + '...';
            return truncatedName + '.' + ext;
        },

        formatFileSize(bytes) {
            if (!bytes) return '0 KB';
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
            return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
        },

        async uploadFile(attachment) {
            try {
                const response = await customerLoanRequestAttachment.uploadFile(attachment.file, {
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.lengthComputable) {
                            attachment.progressing = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        }
                    }
                });

                if (response && response.message_id === 0) {
                    attachment.file_path = response.data.file_path;
                    attachment.size = response.data.size;
                    attachment.size_text = response.data.size_text;
                    attachment.file_type = response.data.mime_type;
                    attachment.progressing = 100;

                    // If requestId exists, save to database immediately
                    if (this.requestId) {
                        await this.saveAttachmentToDatabase(attachment);
                    } else {
                        // Mark as uploaded even without requestId (for create flow)
                        attachment.uploaded = true;
                    }
                }
            } catch (error) {
                attachment.invalid_file = true;
                this.notice({
                    type: 'error',
                    text: error.message || 'File upload failed'
                });
            }
        },

        async saveAttachmentToDatabase(attachment) {
            try {
                const response = await customerLoanRequestAttachment.store(this.requestId, {
                    request_id: this.requestId,
                    file_name: attachment.file_name,
                    file_path: attachment.file_path,
                    file_type: attachment.file_type,
                    file_size: attachment.size,
                    attachment_type: null,
                    description: null
                });

                if (response && response.message_id === 0) {
                    attachment.attachment_id = response.data.attachment_id;
                    attachment.uploaded = true;
                    this.notice({
                        type: 'success',
                        text: this.$t('customerLoanRequest.fileUploadSuccess') || 'File uploaded successfully'
                    });
                }
            } catch (error) {
                this.notice({
                    type: 'error',
                    text: error.message || 'Failed to save attachment'
                });
            }
        },

        deleteAttachment(index) {
            this.deleteIndex = index;
            this.showDeleteModal = true;
        },

        async confirmDelete() {
            const attachment = this.attachmentList[this.deleteIndex];

            try {
                if (attachment.attachment_id) {
                    // Delete from database
                    await customerLoanRequestAttachment.destroy(attachment.attachment_id);
                } else if (attachment.file_path) {
                    // Remove file from storage only
                    await customerLoanRequestAttachment.removeFile(attachment.file_path);
                }

                this.attachmentList.splice(this.deleteIndex, 1);
                this.showDeleteModal = false;
                this.deleteIndex = null;

                this.notice({
                    type: 'success',
                    text: this.$t('customerLoanRequest.fileDeleteSuccess') || 'File deleted successfully'
                });
            } catch (error) {
                this.notice({
                    type: 'error',
                    text: error.message || 'Failed to delete attachment'
                });
            }
        },

        viewFile(filePath) {
            window.open(filePath, '_blank');
        },

        notice(not) {
            this.$Notice[not.type]({
                title: this.$t('customerLoanRequest.attachments') || 'Attachments',
                desc: not.text
            });
        }
    }
};
</script>

<style scoped>
.attachments-container {
    padding: 10px;
}

/* Upload Area */
.upload-area {
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    padding: 40px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #fafafa;
}

.upload-area:hover {
    border-color: #00acac;
    background-color: #f0fafa;
}

.upload-area.drag-over {
    border-color: #00acac;
    background-color: #e6f7f7;
    transform: scale(1.01);
}

.upload-icon {
    font-size: 48px;
    color: #00acac;
    margin-bottom: 15px;
}

.upload-text {
    font-size: 16px;
    color: #333;
    margin-bottom: 8px;
}

.upload-hint {
    font-size: 12px;
    color: #999;
    margin: 0;
}

/* File Cards */
.file-card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: all 0.3s ease;
    position: relative;
    min-height: 180px;
}

.file-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.file-card-error {
    border-color: #ff4d4f;
    background-color: #fff2f0;
}

.file-card-uploading {
    border-color: #1890ff;
    background-color: #e6f7ff;
}

.file-icon-wrapper {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border-radius: 8px;
    margin-bottom: 12px;
}

.file-icon {
    font-size: 28px;
}

.file-info {
    text-align: center;
    width: 100%;
    margin-bottom: 10px;
}

.file-name {
    font-size: 13px;
    font-weight: 500;
    color: #333;
    margin: 0 0 5px 0;
    word-break: break-all;
}

.file-meta {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin: 0;
}

.file-size {
    font-size: 11px;
    color: #999;
}

.file-type-badge {
    font-size: 10px;
    background: #f0f0f0;
    padding: 2px 6px;
    border-radius: 4px;
    color: #666;
    font-weight: 500;
}

/* Progress */
.file-progress {
    width: 100%;
    margin-bottom: 10px;
}

.file-progress .progress {
    height: 6px;
    border-radius: 3px;
    background-color: #e9ecef;
    margin-bottom: 4px;
}

.progress-text {
    font-size: 11px;
    color: #1890ff;
    font-weight: 500;
}

/* Status */
.file-status {
    margin-bottom: 10px;
}

.file-status .badge {
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 12px;
}

/* Actions */
.file-actions {
    display: flex;
    gap: 8px;
}

.file-actions .btn {
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 40px 20px;
    background: #fafafa;
    border-radius: 8px;
    border: 1px dashed #d9d9d9;
}

.empty-icon {
    font-size: 48px;
    color: #d9d9d9;
    margin-bottom: 15px;
}

.empty-text {
    font-size: 14px;
    color: #999;
    margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
    .upload-area {
        padding: 30px 15px;
    }

    .upload-icon {
        font-size: 36px;
    }

    .file-card {
        min-height: 160px;
    }
}
</style>
